import React, { useState, useEffect } from 'react'
import './LegacyCredits.css'
import PageTitle from '../../components/UI/PageTitle'
import Separator from '../../components/UI/Separator'
import { getMonthlyLegacyCredits, verifyLegacyCredit, addLegacyCredit } from '../../api/Index';

const AddLegacyCredits = () => {
    const [legacyCredits, setLegacyCredits] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [verifying, setVerifying] = useState(null); // Track which credit is being verified
    const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1); // 1-based month
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

    const months = [
        { label: 'January', value: 1 },
        { label: 'February', value: 2 },
        { label: 'March', value: 3 },
        { label: 'April', value: 4 },
        { label: 'May', value: 5 },
        { label: 'June', value: 6 },
        { label: 'July', value: 7 },
        { label: 'August', value: 8 },
        { label: 'September', value: 9 },
        { label: 'October', value: 10 },
        { label: 'November', value: 11 },
        { label: 'December', value: 12 }
    ];

    const years = Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - i);

    const fetchLegacyCredits = async () => {
        setLoading(true);
        setError(null);
        
        try {
            console.log('Fetching legacy credits for month:', selectedMonth, 'year:', selectedYear);
            const response = await getMonthlyLegacyCredits(selectedMonth, selectedYear);
            console.log('Legacy credits response:', response);
            setLegacyCredits(Array.isArray(response.data) ? response.data : []);
        } catch (err) {
            console.error('Error fetching legacy credits:', err);
            setError(err.message || 'Failed to load legacy credits');
        } finally {
            setLoading(false);
        }
    };

    const handleVerifyCredit = async (creditId, userId) => {
        if (!creditId) {
            console.error('No credit ID provided for verification');
            setError('No credit ID available for verification');
            return;
        }
        
        setVerifying(creditId);
        try {
            console.log('Verifying legacy credit with ID:', creditId);
            const response = await verifyLegacyCredit(creditId, { status: 'verified' });
            console.log('Verification response:', response);
            
            // Refresh the data after successful verification
            await fetchLegacyCredits();
            console.log('Legacy credit verified successfully');
        } catch (err) {
            console.error('Error verifying legacy credit:', err);
            console.error('Error details:', err.response?.data);
            setError(err.response?.data?.msg || err.response?.data?.message || err.message || 'Failed to verify legacy credit');
        } finally {
            setVerifying(null);
        }
    };

    const handleAddCredit = async (userId) => {
        if (!userId) {
            console.error('No user ID provided for adding credit');
            setError('No user ID available for adding credit');
            return;
        }
        
        setVerifying(`add-${userId}`);
        try {
            console.log('Adding legacy credit for user:', userId);
            const formattedMonth = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}-01`;
            
            const addData = {
                userId,
                month: formattedMonth,
                amount: 1,
                paymentMethod: 'cash'
            };
            console.log('Add credit data:', addData);
            
            const response = await addLegacyCredit(addData);
            console.log('Add credit response:', response);
            
            // Refresh the data after successful addition
            await fetchLegacyCredits();
            console.log('Legacy credit added successfully');
        } catch (err) {
            console.error('Error adding legacy credit:', err);
            console.error('Error details:', err.response?.data);
            setError(err.response?.data?.msg || err.response?.data?.message || err.message || 'Failed to add legacy credit');
        } finally {
            setVerifying(null);
        }
    };

    useEffect(() => {
        fetchLegacyCredits();
    }, [selectedMonth, selectedYear]);

    // Custom user card component for verification interface
    const VerificationUserCard = ({ report }) => {
        const { user, legacyCredit } = report;
        const hasCredit = legacyCredit && legacyCredit.amount > 0;
        const isVerified = legacyCredit && legacyCredit.status === 'verified';
        const creditId = legacyCredit?._id;
        const userId = user?.id || user?._id;
        
        // Debug logging
        console.log('Rendering user card for:', {
            userName: user?.name,
            userId,
            creditId,
            hasCredit,
            isVerified,
            status: legacyCredit?.status,
            amount: legacyCredit?.amount
        });

        return (
            <div style={{
                padding: '15px',
                border: '1px solid #ddd',
                borderRadius: '8px',
                marginBottom: '10px',
                backgroundColor: isVerified ? '#f0f8ff' : '#fff'
            }}>
                <div className='legacy-credit-card'>
                    <div>
                        <h4 style={{ margin: '0 0 5px 0', color: '#333' }}>{user?.name || 'Unknown User'}</h4>
                        <p style={{ margin: '0 0 5px 0', color: '#666', fontSize: '14px' }}>{user?.email || 'No email'}</p>
                        <p style={{ margin: '0 0 5px 0', color: '#888', fontSize: '12px' }}>
                            💰 Amount: KES {legacyCredit?.price || (legacyCredit?.amount * 1000) || 0}
                        </p>
                        <p style={{ margin: '0', color: '#888', fontSize: '11px' }}>
                            📝 Status: {legacyCredit?.status || 'no payment'}
                        </p>
                    </div>
                    <div>
                        {hasCredit ? (
                            isVerified ? (
                                <div style={{
                                    backgroundColor: '#4caf50',
                                    color: 'white',
                                    padding: '8px 16px',
                                    borderRadius: '4px',
                                    fontSize: '14px',
                                    fontWeight: 'bold'
                                }}>
                                    ✅ Verified
                                </div>
                            ) : (
                                <button
                                    onClick={() => handleVerifyCredit(creditId, userId)}
                                    disabled={verifying === creditId}
                                    style={{
                                        backgroundColor: '#2196f3',
                                        color: 'white',
                                        border: 'none',
                                        padding: '8px 16px',
                                        borderRadius: '4px',
                                        cursor: verifying === creditId ? 'not-allowed' : 'pointer',
                                        fontSize: '14px',
                                        fontWeight: 'bold',
                                        opacity: verifying === creditId ? 0.6 : 1
                                    }}
                                >
                                    {verifying === creditId ? 'Verifying...' : '✓ Verify Payment'}
                                </button>
                            )
                        ) : (
                            <button
                                onClick={() => handleAddCredit(userId)}
                                disabled={verifying === `add-${userId}`}
                                style={{
                                    backgroundColor: '#ff9800',
                                    color: 'white',
                                    border: 'none',
                                    padding: '8px 16px',
                                    borderRadius: '4px',
                                    cursor: verifying === `add-${userId}` ? 'not-allowed' : 'pointer',
                                    fontSize: '14px',
                                    fontWeight: 'bold',
                                    opacity: verifying === `add-${userId}` ? 0.6 : 1
                                }}
                            >
                                {verifying === `add-${userId}` ? 'Adding...' : '+ Add Payment'}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    if (loading) {
        return (
            <div>
                <PageTitle title='Manage Legacy Credits' />
                <div style={{ padding: '20px', textAlign: 'center' }}>
                    <p>Loading legacy credits...</p>
                    <p style={{ color: '#666', fontSize: '14px' }}>Please wait while we fetch the data.</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div>
                <PageTitle title='Manage Legacy Credits' />
                <div style={{ padding: '20px', backgroundColor: '#ffe6e6', border: '1px solid #ff0000', borderRadius: '4px', margin: '20px' }}>
                    <h3 style={{ color: 'red', margin: '0 0 10px 0' }}>Error Loading Legacy Credits</h3>
                    <p style={{ color: 'red', margin: '0' }}>{error}</p>
                    <button 
                        onClick={() => window.location.reload()} 
                        style={{ marginTop: '10px', padding: '8px 16px', backgroundColor: '#ff0000', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                    >
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div>
            <PageTitle title='Manage Legacy Credits' />
            
            {/* Header with month/year selection */}
            <div style={{ 
                padding: '20px', 
                backgroundColor: '#f5f5f5', 
                borderRadius: '8px', 
                margin: '20px',
                border: '1px solid #ddd'
            }}>
                <h3 style={{ margin: '0 0 15px 0', color: '#333' }}>Select Period for Legacy Credit Management</h3>
                <div className='select-group' style={{ display: 'flex' }}>
                    <div className='select'>
                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#555' }}>Month:</label>
                        <select 
                            value={selectedMonth} 
                            onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                            style={{ 
                                padding: '8px 12px', 
                                borderRadius: '4px', 
                                border: '1px solid #ccc',
                                fontSize: '14px',
                                minWidth: '120px'
                            }}
                        >
                            {months.map((month) => (
                                <option key={month.value} value={month.value}>
                                    {month.label}
                                </option>
                            ))}
                        </select>
                    </div>
                    
                    <div className='select'>
                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#555' }}>Year:</label>
                        <select 
                            value={selectedYear} 
                            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                            style={{ 
                                padding: '8px 12px', 
                                borderRadius: '4px', 
                                border: '1px solid #ccc',
                                fontSize: '14px',
                                minWidth: '100px'
                            }}
                        >
                            {years.map(year => (
                                <option key={year} value={year}>
                                    {year}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
                <div style={{ marginTop: '10px', color: '#666', fontSize: '14px' }}>
                    Showing records for <strong>{months.find(m => m.value === selectedMonth)?.label} {selectedYear}</strong>
                </div>
            </div>

            {/* Statistics Banner */}
            {legacyCredits.length > 0 && (
                <div style={{ 
                    padding: '15px 20px', 
                    backgroundColor: '#e8f5e8', 
                    border: '1px solid #4caf50', 
                    borderRadius: '4px', 
                    margin: '20px' 
                }}>
                    <p style={{ margin: '0', color: '#2e7d2e', fontSize: '14px' }}>
                        ✓ Found {legacyCredits.length} member record(s) • 
                        {' '}{legacyCredits.filter(r => r.legacyCredit?.status === 'verified').length} verified • 
                        {' '}{legacyCredits.filter(r => r.legacyCredit?.amount > 0 && r.legacyCredit?.status !== 'verified').length} pending verification •
                        {' '}{legacyCredits.filter(r => !r.legacyCredit || r.legacyCredit?.amount === 0).length} no payment
                    </p>
                </div>
            )}

            {/* User list */}
            <div style={{ margin: '20px' }}>
                {legacyCredits.length === 0 ? (
                    <div style={{ 
                        padding: '40px', 
                        textAlign: 'center', 
                        backgroundColor: '#f9f9f9', 
                        borderRadius: '8px',
                        border: '1px solid #ddd'
                    }}>
                        <h4 style={{ color: '#666', margin: '0 0 10px 0' }}>No Records Found</h4>
                        <p style={{ color: '#888', margin: '0' }}>
                            No legacy credits found for {months.find(m => m.value === selectedMonth)?.label} {selectedYear}
                        </p>
                    </div>
                ) : (
                    legacyCredits.map((report, index) => (
                        <VerificationUserCard
                            key={report.user?.id || report.user?._id || index}
                            report={report}
                        />
                    ))
                )}
            </div>
        </div>
    )
}

export default AddLegacyCredits